import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef,
} from "@angular/core";
import * as echarts from "echarts";

export interface Service {
  product_code: string;
  base_cost: number;
  final_cost: number;
}

@Component({
  selector: "app-service-chart",
  template: '<div #chartContainer class="chart-container"></div>',
  styles: [
    `
      .chart-container {
        width: 100%;
        height: 400px;
        overflow: visible;
      }
    `,
  ],
})
export class ServiceChartComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() currentServices: any[] | any = []; // can be weekly object or monthly array
  @Input() previousServices: Service[] = []; // for monthly
  @Input() viewType: "monthly" | "weekly" | "daily" = "monthly";

  @ViewChild("chartContainer") chartContainer!: ElementRef;

  private chartInstance: any;

  ngAfterViewInit(): void {
    if (
      (this.currentServices?.length || this.previousServices?.length) ||
      (this.viewType === "weekly" && Object.keys(this.currentServices || {}).length)
    ) {
      this.initChart();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["currentServices"] ||
      changes["previousServices"] ||
      changes["viewType"]
    ) {
      if (this.chartInstance) {
        this.updateChart();
        setTimeout(() => this.chartInstance?.resize(), 0);
      } else if (this.chartContainer) {
        this.initChart();
      }
    }
  }

  private initChart() {
    this.chartInstance = echarts.init(this.chartContainer.nativeElement);
    this.updateChart();
    window.addEventListener("resize", () => this.chartInstance?.resize());
    setTimeout(() => this.chartInstance?.resize(), 0);
  }

  private updateChart() {
    const emphasisStyle = {
      itemStyle: { shadowBlur: 10, shadowColor: "rgba(0,0,0,0.3)" },
    };

    if (this.viewType === 'weekly') {
      this.updateWeeklyChart(emphasisStyle);
    } else if (this.viewType === 'daily') {
      this.updateDailyChart(emphasisStyle);
    } else if (this.viewType === 'monthly') {
      this.updateMonthlyChart(emphasisStyle);
    }
  }

  private updateMonthlyChart(emphasisStyle: any) {
    const normalize = (code: string) => code?.trim().toLowerCase();

    const previousServices = (this.previousServices || []).map((s) => ({
      ...s,
      normalized_code: normalize(s.product_code),
    }));

    const currentServices = (this.currentServices || []).map((s:any) => ({
      ...s,
      normalized_code: normalize(s.product_code),
    }));

    if (previousServices.length === 0 && currentServices.length === 0) {
      this.showNoData();
      return;
    }

    const previousCodes = previousServices.map((s) => s.normalized_code);
    const currentCodes = currentServices.map((s:any) => s.normalized_code);
    const allServices = Array.from(
      new Set([...previousCodes, ...currentCodes])
    );

    const series = allServices.map((code) => {
      const prev = previousServices.find((s) => s.normalized_code === code);
      const curr = currentServices.find((s:any) => s.normalized_code === code);
      const displayName = prev?.product_code || curr?.product_code || code;

      return {
        name: displayName,
        type: "bar",
        stack: "total",
        emphasis: emphasisStyle,
        data: [prev?.final_cost ?? 0, curr?.final_cost ?? 0],
      };
    });

    const option = {
      legend: {
        data: allServices.map((code) => {
          const prev = previousServices.find((s) => s.normalized_code === code);
          const curr = currentServices.find((s:any) => s.normalized_code === code);
          return prev?.product_code || curr?.product_code || code;
        }),
        bottom: 0,
        left: "center",
      },
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        confine: true,
        renderMode: "html",
        enterable: true,
        formatter: (params: any) => {
          const category = params[0].axisValue;
          let html = `<div style="max-height:150px; overflow-y:auto; padding:5px 10px;">`;
          html += `<strong>${category}</strong><br/>`;
          params.forEach((item: any) => {
            if (item.value > 0) {
              html += `
              <div style="margin:2px 0;">
                <span style="display:inline-block;width:10px;height:10px;background:${item.color};margin-right:6px;border-radius:50%;"></span>
                ${item.seriesName}: <b>${item.value}</b>
              </div>
            `;
            }
          });
          html += `</div>`;
          return html;
        },
      },
      xAxis: {
        type: "category",
        data: ["Previous Month", "Current Month"],
      },
      yAxis: { type: "value", min: 0 },
      grid: { bottom: 120, containLabel: true },
      series,
    };

    this.chartInstance.setOption(option, true);
  }

private updateWeeklyChart(emphasisStyle: any) {
  const weeksData = this.currentServices || {}; // pass event.services

  const weekKeys = Object.keys(weeksData); // ["1week","2week","3week","4week"]

  if (!weekKeys.length) {
    this.showNoData();
    return;
  }

  // Collect all unique services across all weeks
  const allServicesSet = new Set<string>();

  weekKeys.forEach(week => {
    const weekServices = weeksData[week]?.services || [];

    weekServices.forEach((s: any) => {
      const serviceName = s.service; // <-- correct key
      if (serviceName) allServicesSet.add(serviceName);
    });
  });

  const allServices = Array.from(allServicesSet);

  if (!allServices.length) {
    console.warn("No services found in any week!");
    this.showNoData();
    return;
  }

  // Assign consistent colors to services
  const colorPalette = [
    "#5470C6", "#91CC75", "#EE6666", "#FAC858",
    "#73C0DE", "#3BA272", "#FC8452", "#9A60B4", "#EA7CCC"
  ];
  const serviceColors: Record<string, string> = {};
  allServices.forEach((service, idx) => {
    serviceColors[service] = colorPalette[idx % colorPalette.length];
  });

  // Build series for ECharts
  const series = allServices.map(code => {
    const displayName = code;

    const data = weekKeys.map(week => {
      const weekServices = weeksData[week]?.services || [];
      const s = weekServices.find((item: any) => item.service === code);
      return s?.final_cost ?? 0;
    });


    return {
      name: displayName,
      type: "bar",
      stack: "total",
      emphasis: emphasisStyle,
      itemStyle: { color: serviceColors[displayName] },
      data
    };
  });


  const option = {
    legend: {
      data: allServices,
      bottom: 0,
      left: "center",
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      confine: true,
      renderMode: "html",
      enterable: true,
      formatter: (params: any) => {
        const category = params[0].axisValue;
        let html = `<div style="max-height:150px; overflow-y:auto; padding:5px 10px;">`;
        html += `<strong>${category}</strong><br/>`;
        params.forEach((item: any) => {
          if (item.value > 0) {
            html += `
              <div style="margin:2px 0;">
                <span style="display:inline-block;width:10px;height:10px;background:${item.color};margin-right:6px;border-radius:50%;"></span>
                ${item.seriesName}: <b>${item.value}</b>
              </div>
            `;
          }
        });
        html += `</div>`;
        return html;
      },
    },
    xAxis: {
      type: "category",
      data: weekKeys.map(week => weeksData[week].billing_period),
    },
    yAxis: { type: "value", min: 0 },
    grid: { bottom: 120, containLabel: true },
    series
  };


  this.chartInstance.setOption(option, true);
}

private updateDailyChart(emphasisStyle: any) {
  const daysData = this.currentServices || {}; // pass event.services

  const dayKeys = Object.keys(daysData).sort((a,b) => new Date(a).getTime() - new Date(b).getTime());

  if (!dayKeys.length) {
    this.showNoData();
    return;
  }

  // Collect all unique services across all days
  const allServicesSet = new Set<string>();
  dayKeys.forEach(day => {
    const dayServices = daysData[day]?.services || [];
    dayServices.forEach((s: any) => {
      if (s.service) allServicesSet.add(s.service);
    });
  });

  const allServices = Array.from(allServicesSet);

  if (!allServices.length) {
    console.warn("No services found in any day!");
    this.showNoData();
    return;
  }

  // Assign consistent colors to services
  const colorPalette = [
    "#5470C6", "#91CC75", "#EE6666", "#FAC858",
    "#73C0DE", "#3BA272", "#FC8452", "#9A60B4", "#EA7CCC"
  ];
  const serviceColors: Record<string, string> = {};
  allServices.forEach((service, idx) => {
    serviceColors[service] = colorPalette[idx % colorPalette.length];
  });

  // Build series for ECharts
  const series = allServices.map(code => {
    const data = dayKeys.map(day => {
      const dayServices = daysData[day]?.services || [];
      const s = dayServices.find((item: any) => item.service === code);
      return s?.final_cost ?? 0;
    });

    
    return {
      name: code,
      type: "bar",
      stack: "total",
      emphasis: emphasisStyle,
      itemStyle: { color: serviceColors[code] },
      data
    };
  });

  const option = {
    legend: {
      data: allServices,
      bottom: 0,
      left: "center",
    },
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      confine: true,
      renderMode: "html",
      enterable: true,
      formatter: (params: any) => {
        const category = params[0].axisValue;
        let html = `<div style="max-height:150px; overflow-y:auto; padding:5px 10px;">`;
        html += `<strong>${category}</strong><br/>`;
        params.forEach((item: any) => {
          if (item.value > 0) {
            html += `
              <div style="margin:2px 0;">
                <span style="display:inline-block;width:10px;height:10px;background:${item.color};margin-right:6px;border-radius:50%;"></span>
                ${item.seriesName}: <b>${item.value}</b>
              </div>
            `;
          }
        });
        html += `</div>`;
        return html;
      },
    },
    xAxis: {
      type: "category",
      data: dayKeys.map(day => daysData[day].billing_period), // show billing_period
    },
    yAxis: { type: "value", min: 0 },
    grid: { bottom: 120, containLabel: true },
    series
  };

  this.chartInstance.setOption(option, true);
}


  private showNoData() {
    const option = {
      title: { show: false },
      xAxis: { show: false },
      yAxis: { show: false },
      series: [] as echarts.SeriesOption[],
      graphic: {
        type: "text",
        left: "center",
        top: "middle",
        style: {
          text: "No data found",
          fontSize: 18,
          fontWeight: "bold",
          fill: "#999",
        },
      },
    };
    this.chartInstance.setOption(option, true);
  }

  ngOnDestroy(): void {
    if (this.chartInstance) this.chartInstance.dispose();
  }
}